import { Info } from "@mui/icons-material";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation } from "@tanstack/react-query";
import { CardSize, MapPdfPayload } from "griptonite-api-client/endpoints/venues";
import { useMemo } from "react";
import { useLocalStorage } from "react-use";

import { useRoutesByUuids } from "@/hooks/routes";

import api from "../../core/api";
import { useCurrentClimbType, useCurrentVenueQuery, useCurrentVenueUuid } from "../../hooks/venues";
import { useModal } from "./useModal";

type SportCardsPayload = Parameters<typeof api.venues.generateSportCardsPdf>[1];
type SportCardSize = NonNullable<SportCardsPayload["cardSize"]>;
type SportCardPageSize = NonNullable<SportCardsPayload["pageSize"]>;

type PrintOptions = {
  assetType: string;
  sportCardSize: SportCardSize;
  pageSize: SportCardPageSize;
  cardSize: CardSize;
  textSize: "standard" | "large";
};

const LETTER_PAPER_COUNTRIES = ["US", "CA"];

const fnMap = {
  sportCards: api.venues.generateSportCardsPdf,
  lineCards: api.venues.generateLineCardsPdf,
  map: api.venues.generateMapPdf,
};

const useGenerateMutation = () => {
  const currentVenueUuid = useCurrentVenueUuid();
  return useMutation({
    mutationFn: async ({
      routeUuids,
      type,
      formData,
      lineUuids,
    }: {
      routeUuids: string[];
      type: string;
      formData: FormData;
      lineUuids?: string[];
    }) => {
      if (!currentVenueUuid) {
        throw new Error("Missing venue uuid");
      }
      if (!(type in fnMap)) {
        throw new Error("Invalid type");
      }
      const apiEndpoint = fnMap[type as keyof typeof fnMap];
      let payload;
      if (type === "map") {
        payload = {
          routes: routeUuids,
          name: formData.get("name")?.toString().trim() || null,
          description: formData.get("description")?.toString().trim() || null,
        } as MapPdfPayload;
      } else if (type === "lineCards") {
        payload = {
          lines: lineUuids,
          isBasic: formData.get("textSize") === "large",
          cardSize: formData.get("cardSize") as CardSize,
        };
      } else {
        payload = {
          routes: routeUuids,
          cardSize: formData.get("sportCardSize") as SportCardSize,
          pageSize: formData.get("pageSize") as SportCardPageSize,
        };
      }
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      const { url } = await apiEndpoint(currentVenueUuid, payload);

      // repeatedly poll URL until it's ready or a timeout is reached
      let triesCount = 0;
      while (triesCount < 60) {
        try {
          const result = await fetch(url, {
            method: "HEAD",
          });
          if (result.ok) {
            return url;
          } else {
            triesCount++;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } catch (e) {
          triesCount++;
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
      throw new Error("Request timed out. Please try again or contact support");
    },
  });
};

export default function PrintRoutes({ routeUuids }: { routeUuids: string[] }) {
  const { close } = useModal();
  const climbType = useCurrentClimbType();

  const routes = useRoutesByUuids(routeUuids);

  const lineUuids = useMemo(() => {
    return [...new Set(routes.map((route) => route.startLine?.uuid).filter(Boolean))] as string[];
  }, [routes]);

  let choices;
  if (climbType === "SPORT") {
    choices = [
      {
        label: "Route cards",
        value: "sportCards",
      },
      {
        label: "Line cards",
        value: "lineCards",
      },
    ];
  } else {
    choices = [
      {
        label: "Map",
        value: "map",
      },
    ];
  }

  const { data: venue } = useCurrentVenueQuery();
  const [savedOptions, setSavedOptions] = useLocalStorage<Partial<PrintOptions>>(
    `printRoutes.options.${climbType}`,
    {},
  );

  // Anything the user hasn't picked before falls back to a default
  const options: PrintOptions = {
    assetType: choices[0].value,
    sportCardSize: "94x120",
    pageSize: LETTER_PAPER_COUNTRIES.includes(venue?.country ?? "") ? "letter" : "A4",
    cardSize: "A6",
    textSize: "standard",
    ...savedOptions,
  };
  if (!choices.some((choice) => choice.value === options.assetType)) {
    options.assetType = choices[0].value;
  }
  const { assetType } = options;

  const setOption = <K extends keyof PrintOptions>(key: K, value: PrintOptions[K] | null) => {
    if (value) {
      setSavedOptions({ ...savedOptions, [key]: value });
    }
  };

  console.log({ choices, assetType });

  const mutation = useGenerateMutation();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation
      .mutateAsync({
        routeUuids,
        type: assetType,
        formData: new FormData(e.currentTarget as HTMLFormElement),
        lineUuids,
      })
      .then(
        (url) => {
          window.open(url, "_blank");
          close();
        },
        (error) => {
          alert(error.message);
        },
      );
  };

  return (
    <ModalDialog>
      <ModalClose />
      <form onSubmit={onSubmit}>
        <DialogTitle>
          {routeUuids.length === 1 ? "Print route" : `Print ${routeUuids.length} routes`}
        </DialogTitle>
        <DialogContent sx={{ width: 400 }}>
          <Stack spacing={1.5} sx={{ marginTop: 1 }}>
            <FormControl>
              <FormLabel>Asset type</FormLabel>
              <Select
                name="assetType"
                value={assetType}
                onChange={(_, value) => setOption("assetType", value)}
              >
                {choices.map((choice) => (
                  <Option key={choice.value} value={choice.value}>
                    {choice.label}
                  </Option>
                ))}
              </Select>
            </FormControl>
            {assetType === "sportCards" && (
              <>
                <FormControl>
                  <FormLabel>Card size</FormLabel>
                  <Select
                    name="sportCardSize"
                    value={options.sportCardSize}
                    onChange={(_, value) => setOption("sportCardSize", value)}
                  >
                    <Option value="94x120">94 × 120 mm</Option>
                    <Option value="3x4">3 × 4 in</Option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Page size</FormLabel>
                  <Select
                    name="pageSize"
                    value={options.pageSize}
                    onChange={(_, value) => setOption("pageSize", value)}
                  >
                    <Option value="A4">A4</Option>
                    <Option value="letter">US Letter</Option>
                  </Select>
                </FormControl>
              </>
            )}
            {assetType === "lineCards" && (
              <>
                <FormControl>
                  <FormLabel>Card size</FormLabel>
                  <Select
                    name="cardSize"
                    value={options.cardSize}
                    onChange={(_, value) => setOption("cardSize", value)}
                  >
                    <Option value="A5">A5</Option>
                    <Option value="A6">A6</Option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Text size</FormLabel>
                  <Select
                    name="textSize"
                    value={options.textSize}
                    onChange={(_, value) => setOption("textSize", value)}
                  >
                    <Option value="standard">Standard</Option>
                    <Option value="large">Large</Option>
                  </Select>
                </FormControl>
              </>
            )}
            {assetType === "map" && (
              <>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input type="text" name="name" placeholder="e.g. Pick of the Week" />
                </FormControl>
                <FormControl>
                  <FormLabel>Description (optional)</FormLabel>
                  <Textarea
                    name="description"
                    minRows={3}
                    maxRows={6}
                    placeholder="e.g. Our top routes this week"
                  />
                </FormControl>
              </>
            )}
            {routeUuids.length > 50 && climbType === "SPORT" ? (
              <Alert color="neutral" startDecorator={<Info />}>
                <Typography level="body-sm" textAlign="left" textColor="neutral.708">
                  <strong>Generating cards can take up to a minute.</strong>
                  <br /> If it’s not working, try printing a few lines at a time.
                </Typography>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            color="primary"
            sx={{ flex: 1 }}
            disabled={mutation.isPending}
            loading={mutation.isPending}
            loadingPosition="start"
          >
            {mutation.isPending ? "Generating..." : "Generate PDF"}
          </Button>
          <Button
            onClick={() => {
              close();
            }}
            color="neutral"
            variant="soft"
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </form>
    </ModalDialog>
  );
}
